/* Compact PDP thumbnails: a bounded white-margin analysis once per loaded image.
   No catalogue crawl, DOM observer, polling, full-size image editing or gallery mutation. */
(function () {
  'use strict';
  if (window.__mipPdpSwatchesInstalled) return;
  window.__mipPdpSwatchesInstalled = true;
  var inspected = new WeakSet();
  var tileRatio = 1.5;

  function normaliseWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function titleCase(value) {
    return normaliseWhitespace(value)
      .toLowerCase()
      .replace(/\b\w/g, function (match) { return match.toUpperCase(); });
  }

  function splitWords(value) {
    return normaliseWhitespace(value).split(' ').filter(Boolean);
  }

  function commonPrefixWordCount(wordLists) {
    if (!wordLists.length) return 0;
    var shortest = wordLists.reduce(function (min, words) { return Math.min(min, words.length); }, wordLists[0].length);
    var count = 0;
    for (var index = 0; index < shortest; index += 1) {
      var word = wordLists[0][index].toLowerCase();
      var allMatch = wordLists.every(function (words) {
        return (words[index] || '').toLowerCase() === word;
      });
      if (!allMatch) break;
      count += 1;
    }
    return count;
  }

  function commonSuffixWordCount(wordLists, prefixCount) {
    if (!wordLists.length) return 0;
    var shortest = wordLists.reduce(function (min, words) { return Math.min(min, words.length - prefixCount); }, wordLists[0].length - prefixCount);
    var count = 0;
    for (var offset = 1; offset <= shortest; offset += 1) {
      var word = wordLists[0][wordLists[0].length - offset].toLowerCase();
      var allMatch = wordLists.every(function (words) {
        return (words[words.length - offset] || '').toLowerCase() === word;
      });
      if (!allMatch) break;
      count += 1;
    }
    return count;
  }

  function deriveColourNames(products) {
    var titles = products.map(function (product) { return splitWords(product.title); });
    var prefixCount = commonPrefixWordCount(titles);
    var suffixCount = commonSuffixWordCount(titles, prefixCount);

    return products.map(function (product, index) {
      var words = titles[index].slice(prefixCount, titles[index].length - suffixCount);
      var fallback = product.title || product.handle || 'See option';
      return titleCase(words.join(' ')) || normaliseWhitespace(fallback);
    });
  }


  function imageURL(value) {
    if (!value) return '';
    try {
      var url = new URL(String(value).replace('{width}', '240'), location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';
      // Bound the existing thumbnail request instead of downloading a zoom original.
      url.searchParams.set('width', '240');
      return url.href;
    } catch (ignore) { return ''; }
  }
  function trimWhiteMargins(image) {
    if (inspected.has(image) || !image.naturalWidth || !image.naturalHeight) return;
    inspected.add(image);
    try {
      var factor = Math.min(1, 128 / Math.max(image.naturalWidth, image.naturalHeight));
      var w = Math.max(1, Math.round(image.naturalWidth * factor));
      var h = Math.max(1, Math.round(image.naturalHeight * factor));
      var canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx || w < 8 || h < 8) return;
      ctx.drawImage(image, 0, 0, w, h);
      var pixels = ctx.getImageData(0, 0, w, h).data;
      var corners = [0, (w - 1) * 4, (h - 1) * w * 4, (w * h - 1) * 4];
      // A non-white/complex background is left intact. Never guess a shoe's outline.
      var safe = corners.every(function (i) { return pixels[i + 3] < 16 || (Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) >= 238 && Math.max(pixels[i], pixels[i + 1], pixels[i + 2]) - Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) <= 18); });
      if (!safe) return;
      var left = w, right = -1, top = h, bottom = -1, occupied = 0;
      for (var y = 0; y < h; y += 1) {
        for (var x = 0; x < w; x += 1) {
          var i = (y * w + x) * 4;
          var low = Math.min(pixels[i], pixels[i + 1], pixels[i + 2]);
          var high = Math.max(pixels[i], pixels[i + 1], pixels[i + 2]);
          var white = pixels[i + 3] < 16 || (low >= 238 && high - low <= 18);
          if (!white) {
            left = Math.min(left, x); right = Math.max(right, x);
            top = Math.min(top, y); bottom = Math.max(bottom, y); occupied += 1;
          }
        }
      }
      if (occupied < w * h * .008 || right < left || bottom < top) return;
      var margin = Math.max(3, Math.ceil(Math.max(w, h) * .035));
      left = Math.max(0, left - margin); right = Math.min(w, right + margin + 1);
      top = Math.max(0, top - margin); bottom = Math.min(h, bottom + margin + 1);
      if (right - left >= w * .96 && bottom - top >= h * .96) return;
      // Convert sample-space bounds to fractions of the ORIGINAL photo so rounding
      // the small analysis canvas never changes its natural proportions.
      var cropLeft = left / w, cropTop = top / h;
      var cropWidth = (right - left) / w, cropHeight = (bottom - top) / h;
      var aspect = image.naturalWidth / image.naturalHeight;
      var scale = Math.min(tileRatio * .96 / (cropWidth * aspect), .96 / cropHeight);
      var fullWidth = aspect * scale, fullHeight = scale;
      image.style.setProperty('--pdp-thumb-width', (100 * fullWidth / tileRatio).toFixed(4) + '%');
      image.style.setProperty('--pdp-thumb-height', (100 * fullHeight).toFixed(4) + '%');
      image.style.setProperty('--pdp-thumb-left', (100 * ((tileRatio - cropWidth * fullWidth) / 2 - cropLeft * fullWidth) / tileRatio).toFixed(4) + '%');
      image.style.setProperty('--pdp-thumb-top', (100 * ((1 - cropHeight * fullHeight) / 2 - cropTop * fullHeight)).toFixed(4) + '%');
      image.setAttribute('data-pdp-trimmed', '');
      canvas.width = canvas.height = 1;
    } catch (ignore) {
      // An unreadable canvas/CORS restriction keeps the original contained thumbnail.
      // Do not hide the image, invent a crop or retry continuously.
    }
  }
  function siblings(container) {
    var source = container.querySelector('[data-pdp-colour-products]');
    if (!source) return Promise.reject(new Error('Colour choices unavailable'));
    try {
      var products = JSON.parse(source.textContent);
      if (!Array.isArray(products)) throw new Error('Invalid colour choices');
      return Promise.resolve(products);
    } catch (error) { return Promise.reject(error); }
  }
  function build(container) {
    if (container.dataset.pdpSwatchesReady) return;
    var styleId = container.dataset.styleId;
    var currentHandle = container.dataset.currentHandle;
    var list = container.querySelector('[data-swatch-list]');
    var labelValue = container.querySelector('[data-swatch-label-value]');
    if (!styleId || !list) return;
    container.dataset.pdpSwatchesReady = 'pending';
    var initialLabel = labelValue ? labelValue.textContent : '';
    function label(value) { if (labelValue && labelValue.textContent !== value) labelValue.textContent = value; }
    function restore() {
      list.querySelectorAll('.is-previewing').forEach(function (item) { item.classList.remove('is-previewing'); });
      var active = list.querySelector('.pdp-swatches__swatch.is-active');
      label(active ? active.dataset.colourName : initialLabel);
    }
    function preview(swatch) {
      restore();
      if (!swatch.classList.contains('is-active')) swatch.classList.add('is-previewing');
      label(swatch.dataset.colourName);
    }
    siblings(container).then(function (products) {
      if (!container.isConnected) return;
      var seen = new Set();
      products = products.filter(function (product) {
        if (!product || !product.handle || seen.has(product.id || product.handle)) return false;
        seen.add(product.id || product.handle); return true;
      });
      if (products.length <= 1) { container.dataset.pdpSwatchesReady = 'ready'; return; }
      var colourNames = deriveColourNames(products), fragment = document.createDocumentFragment();
      products.forEach(function (product, index) {
        var href;
        try { href = new URL(product.url, location.origin); if (href.origin !== location.origin) return; } catch (ignore) { return; }
        var swatch = document.createElement('a');
        var name = colourNames[index] || normaliseWhitespace(product.colour_name) || normaliseWhitespace(product.title);
        swatch.className = 'pdp-swatches__swatch' + (product.handle === currentHandle ? ' is-active' : '');
        swatch.href = href.href; swatch.title = product.title || name;
        swatch.setAttribute('aria-label', product.title || name);
        swatch.dataset.colourName = name;
        if (product.handle === currentHandle) swatch.setAttribute('aria-current', 'true');
        var frame = document.createElement('span'); frame.className = 'pdp-swatches__image-frame'; frame.setAttribute('aria-hidden', 'true');
        var source = product.featured_image && (product.featured_image.url || product.featured_image.src);
        var url = imageURL(source);
        if (url) {
          var img = document.createElement('img');
          img.alt = ''; img.width = 240; img.height = 240;
          img.loading = 'lazy'; img.decoding = 'async'; img.crossOrigin = 'anonymous';
          img.addEventListener('load', function () { trimWhiteMargins(img); });
          img.addEventListener('error', function () {
            if (img.hasAttribute('crossorigin')) {
              img.removeAttribute('crossorigin'); img.src = url; // one non-CORS fallback only
            } else {
              var placeholder = document.createElement('span'); placeholder.className = 'pdp-swatches__placeholder'; placeholder.textContent = name;
              swatch.replaceChildren(placeholder);
            }
          });
          img.src = url; frame.appendChild(img); swatch.appendChild(frame);
        } else {
          var placeholder = document.createElement('span'); placeholder.className = 'pdp-swatches__placeholder'; placeholder.textContent = name; swatch.appendChild(placeholder);
        }
        fragment.appendChild(swatch);
      });
      list.replaceChildren(fragment);
      container.dataset.pdpSwatchesReady = 'ready';
      list.addEventListener('pointerover', function (event) {
        if (event.pointerType === 'touch' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        var swatch = event.target.closest('.pdp-swatches__swatch');
        if (swatch && (!event.relatedTarget || !swatch.contains(event.relatedTarget))) preview(swatch);
      });
      list.addEventListener('pointerout', function (event) {
        if (event.pointerType === 'touch') return;
        var swatch = event.target.closest('.pdp-swatches__swatch');
        if (swatch && (!event.relatedTarget || !swatch.contains(event.relatedTarget))) restore();
      });
      list.addEventListener('focusin', function (event) { var swatch = event.target.closest('.pdp-swatches__swatch'); if (swatch) preview(swatch); });
      list.addEventListener('focusout', function (event) { if (!event.relatedTarget || !list.contains(event.relatedTarget)) restore(); });
      restore();
      // Intentionally no click handler: first mobile tap follows the original product link.
    }).catch(function () { container.dataset.pdpSwatchesReady = 'error'; });
  }
  function init(scope) { (scope || document).querySelectorAll('[data-pdp-swatches]').forEach(build); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  else init();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
